import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import SvgIcon from "@mui/material/SvgIcon";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import { PasswordInputProps } from "types";
import { useText } from "context/TextProvider";

const LABEL_SX: SxProps<Theme> = { display: "block", fontSize: "0.875rem", fontWeight: 500, mb: "0.25rem", color: "text.secondary" };

const PasswordInput = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className = "",
  name = "password",
  id,
  label,
}: PasswordInputProps) => {
  const text = useText();
  const resolvedPlaceholder = placeholder ?? text.passwordInput.defaultPlaceholder;
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      {label && (
        <Typography component="label" htmlFor={id} sx={LABEL_SX}>
          {label}
        </Typography>
      )}
      <TextField
        id={id}
        name={name}
        type={showPassword ? "text" : "password"}
        value={value}
        placeholder={resolvedPlaceholder}
        onChange={onChange}
        disabled={disabled}
        fullWidth
        variant="outlined"
        className={["login-config-input", className].filter(Boolean).join(" ")}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="button"
                  aria-label={showPassword ? text.passwordInput.hidePasswordAriaLabel : text.passwordInput.showPasswordAriaLabel}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  sx={{ color: "inherit" }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: "1rem",
          "& .MuiOutlinedInput-root": {
            height: 56,
            "& fieldset": {
              borderColor: "divider",
            },
            "&:hover fieldset": {
              borderColor: "text.primary",
            },
            "&.Mui-focused fieldset": {
              borderColor: "text.primary",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
          },
        }}
      />
    </>
  );
};

const VisibilityIcon = () => (
  <SvgIcon fontSize="small" viewBox="0 0 24 24">
    <path
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
    <path
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z"
    />
  </SvgIcon>
);

const VisibilityOffIcon = () => (
  <SvgIcon fontSize="small" viewBox="0 0 24 24">
    <path
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.875 18.825A10.05 10.05 0 0 1 12 19c-5.523 0-10-4.477-10-10 0-1.306.252-2.552.708-3.694"
    />
    <path
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.88 9.88a3 3 0 1 0 4.243 4.243M3 3l18 18"
      />
  </SvgIcon>
);

export default PasswordInput;
